import { GQL } from '@aline/graphql';
import { capitalCase } from 'change-case';
import { format as formatDate } from 'date-fns';
import { Editor } from 'slate';

import type {
  CarbonCopyEvent,
  EnvelopeCertificate,
  EnvelopeMeta,
  FileDetail,
  SignerEvent,
} from '../../shared/envelope-certificate';
import { dateFormatString } from '../../shared/envelope-certificate';
import type { ImageMap } from '../../types';
import type { UUID } from '../../types/type-helpers';
import { createDefaultText } from '../defaults/text';
import type { ContentNodes, Format, ParagraphElement } from '../internal';
import {
  AlineTransforms,
  buildRenderModel,
  createDefaultDocumentParts,
  createDefaultParagraph,
  createParagraphWithChildren,
  createSignatureFieldElement,
  defaultStyle,
  EditingMode,
  noTableBorders,
  Queries,
} from '../internal';

const BOLD: Format = { bold: { active: true, origin: 'USER_APPLIED' } };
const UNBOLD: Format = { bold: { active: false, origin: 'INHERITED' } };
const SMALL_FONT: Format = { fontSize: { origin: 'USER_APPLIED', size: 10 } };
const LARGE_FONT: Format = { fontSize: { origin: 'USER_APPLIED', size: 16 } };
const LARGEST_FONT: Format = { fontSize: { origin: 'USER_APPLIED', size: 24 } };

type NullableElement = ContentNodes | null;
type RowTriple = [NullableElement, NullableElement, NullableElement];

const spacer: RowTriple = [null, null, null];

export const CertificateTransforms = {
  /**
   * Creates an EnvelopeCertificate document from a certificateJSON summary
   */
  certificateJSONtoAlineDoc(certificateJSON: EnvelopeCertificate): {
    editor: Editor;
    imageMap: ImageMap;
  } {
    const { carbonCopyEvents, envelopeMeta, fileDetails, imageMap, signerEvents } = certificateJSON;
    const authorId = envelopeMeta.envelopeCreator.id;
    const editor = AlineTransforms.createServerEditorFromJson(createDefaultDocumentParts(), {
      state: {
        editingUserId: authorId,
        isInSigningCeremony: false,
        isReadOnly: false,
        isSigned: false,
        userDeclinedSigning: false,
      },
    });

    // Widen margins to allow more content in each cell.
    AlineTransforms.updatePageDimensions(editor, {
      authorId,
      editingMode: EditingMode.EDITING,
      newPageDimensions: {
        marginLeft: 0.25,
        marginRight: 0.25,
      },
    });

    // Initialize selection at end of document.
    const [, docPath] = Queries.getDocument(editor);
    AlineTransforms.select(editor, Editor.end(editor, docPath));

    /**
     * Build document nodes.
     * The document is one large table, 3 columns across, with 0pt thickness borders.
     */
    const rows: RowTriple[] = [
      ...makeEnvelopeMetaRows(envelopeMeta, fileDetails),
      // Padding
      spacer,
      ...makeSignerRows(signerEvents, authorId),
      // Padding
      spacer,
      ...makeCCRows(carbonCopyEvents),
    ];

    // The render model is required to insert nodes
    buildRenderModel(editor);

    // Insert table and insert built nodes.
    AlineTransforms.withoutNormalizing(editor, () => {
      // Document title.
      AlineTransforms.insertContentNodes(editor, {
        authorId,
        editingMode: EditingMode.EDITING,
        nodes: [
          createDefaultParagraph({
            text: 'Certificate of Completion',
            textProps: { ...BOLD, ...LARGEST_FONT },
          }),
        ],
        select: 'end',
      });

      // Create the shell table
      AlineTransforms.insertTable(editor, {
        authorId,
        editingMode: EditingMode.EDITING,
        newTableProps: {
          columnCount: 3,
          rowCount: 1, // More rows will be added automatically by tabbing.
          tableBorders: noTableBorders,
        },
      });

      for (const row of rows) {
        for (const cellContentNodes of row) {
          if (cellContentNodes) {
            // Insert each cell.
            AlineTransforms.insertContentNodes(editor, {
              authorId,
              editingMode: EditingMode.EDITING,
              nodes: cellContentNodes,
            });
          }

          // Tab to next cell.
          AlineTransforms.tab(editor, {
            authorId,
            direction: 'forward',
            editingMode: EditingMode.EDITING,
          });
        }
      }
    });

    return { editor, imageMap };
  },
};

/**
 * Basic details of the envelopes and constituent files.
 */
function makeEnvelopeMetaRows(envelopeMeta: EnvelopeMeta, fileDetails: FileDetail[]): RowTriple[] {
  const envelopeIdentifierCell = [
    `Envelope ID: ${envelopeMeta.envelopeId}`,
    `Title: ${envelopeMeta.title}`,
    `Status: ${capitalCase(envelopeMeta.status)}`,
    `Time Zone: ${envelopeMeta.timeZone}`,
  ].map(text => createSmallFontParagraph(text));

  const fileListingCell = [
    `Document Count: ${fileDetails.length}`,
    `Envelope Contents:`,
    ...fileDetails.map(f => f.name),
  ].map(text => createSmallFontParagraph(text));

  const envelopeSenderCell = [
    'Sender: ',
    envelopeMeta.envelopeCreator.displayName,
    envelopeMeta.envelopeCreator.email,
  ].map(text => createSmallFontParagraph(text));

  return [[envelopeIdentifierCell, fileListingCell, envelopeSenderCell]];
}

/**
 * Indicates that each signer has agreed to accept e-signature as valid.
 */
const disclosureRow: RowTriple = [
  [
    createDefaultParagraph({
      text: 'Electronic record and signature disclosure',
      textProps: { ...BOLD, ...SMALL_FONT },
    }),
  ],
  null,
  null,
];

/**
 * Rows for Signing EnvelopeRecipients.
 */
function makeSignerRows(signerEvents: SignerEvent[], authorId: UUID): RowTriple[] {
  /* Signer header */
  const signersHeader: RowTriple = [
    [createDefaultParagraph({ text: `Signer Events`, textProps: { ...BOLD, ...LARGE_FONT } })],
    [createDefaultParagraph({ text: `Signature`, textProps: { ...BOLD, ...LARGE_FONT } })],
    [createDefaultParagraph({ text: `Timestamps`, textProps: { ...BOLD, ...LARGE_FONT } })],
  ];

  /** Each Signer Event */
  const signerEventRows = signerEvents.map<[RowTriple, RowTriple]>(
    ({ displayName, email, firstSentAt, signatures, signedAt, signingIpAddress, viewedAt }) => {
      const signerDetail = [
        createSmallFontParagraph(`Signer:`),
        createSmallFontParagraph(displayName),
        createSmallFontParagraph(email),
      ];

      const signatureCol = [
        createSmallFontParagraph(`IP Address: ${displayIPAddress(signingIpAddress)}`),
        // A given signature image should appear once.
        ...signatures.map(({ fieldId, signatureImgId }) => {
          /**
           * NOTE: Slightly smaller than the table no-border cell width.
           * TODO: Prefer to calculate off of table cell width.
           */
          const imgWidthPx = 230;

          const sigFieldElement = createSignatureFieldElement({
            authorId,
            editingMode: EditingMode.EDITING,
            props: {
              fieldId,
              imgWidthPx,
              signatureImgId,
              variant: GQL.FieldVariant.Signature,
            },
            textFormats: defaultStyle.styles,
          });

          return createParagraphWithChildren({
            children: [createDefaultText(), sigFieldElement, createDefaultText()],
          });
        }),
      ];
      const timestamps = [
        `Sent at: ${firstSentAt ? formatDate(firstSentAt, dateFormatString) : 'N/A'}`,
        `Viewed at: ${viewedAt ? formatDate(viewedAt, dateFormatString) : 'N/A'}`,
        `Signed at: ${signedAt ? formatDate(signedAt, dateFormatString) : 'N/A'}`,
      ].map(createSmallFontParagraph);

      const signerRow: RowTriple = [signerDetail, signatureCol, timestamps];
      return [signerRow, disclosureRow];
    }
  );

  return [signersHeader, ...signerEventRows.flat()];
}

/**
 * Rows for Carbon Copy EnvelopeRecipients.
 */
function makeCCRows(carbonCopyEvents: CarbonCopyEvent[]): RowTriple[] {
  // Don't include CC section if no CC's
  if (!carbonCopyEvents.length) return [];

  const carbonCopyHeader: RowTriple = [
    [createDefaultParagraph({ text: `Carbon Copy Events`, textProps: { ...BOLD, ...LARGE_FONT } })],
    [createDefaultParagraph({ text: `Status`, textProps: { ...BOLD, ...LARGE_FONT } })],
    [createDefaultParagraph({ text: `Timestamps`, textProps: { ...BOLD, ...LARGE_FONT } })],
  ];

  const carbonCopyEventRows = carbonCopyEvents.map<RowTriple[]>(
    ({ displayName, email, firstSentAt, viewedAt }) => {
      const signerDetail = [
        createSmallFontParagraph('Recipient:'),
        createSmallFontParagraph(displayName),
        /** Skip displaying email if displayName is the same as email. */
        ...(email === displayName ? [] : [createSmallFontParagraph(email)]),
      ];
      const statusCol = [createSmallFontParagraph(`COPIED`)];
      const timestamps = [
        createSmallFontParagraph(
          `Sent at: ${firstSentAt ? formatDate(firstSentAt, dateFormatString) : 'N/A'}`
        ),
        createSmallFontParagraph(
          `Viewed at: ${viewedAt ? formatDate(viewedAt, dateFormatString) : 'N/A'}`
        ),
      ];

      const ccRow: RowTriple = [signerDetail, statusCol, timestamps];

      return [ccRow, disclosureRow, spacer];
    }
  );

  return [
    /* Carbon Copy header */
    carbonCopyHeader,
    /** Each Carbon Copy Event */
    ...carbonCopyEventRows.flat(),
  ];
}

/**
 * The majority of fonts in the table cells can be smaller.
 */
function createSmallFontParagraph(text: string): ParagraphElement {
  return createDefaultParagraph({
    text,
    textProps: { ...UNBOLD, ...SMALL_FONT },
  });
}

/**
 * Displays the IP address of the request.
 *
 * NOTE: If insufficient information for a full IPv6, renders it in IPv4.
 */
function displayIPAddress(ipAddress: string | null): string {
  if (!ipAddress) return 'N/A';

  // If there are multiple in the list, prefer the first as that's closest to the client.
  const [clientIP] = ipAddress.split(', ');

  // If IPv4, remove the `::ffff:` prefix to render it nicely.
  return clientIP.startsWith('::ffff:') ? clientIP.replace('::ffff:', '') : clientIP;
}
